import React, { useEffect, useRef, useState, useMemo } from 'react'
import styled from 'styled-components'
import * as R from 'ramda'

import ApexChart from 'react-apexcharts'
import {
  TimeChart,
  convertNullsToZero,
  sortByWeekOrder
} from 'modules/learningTime/utils/TimeChart'
import useOutsideClick from 'hooks/useOutsideClick'

import { noTimezoneDate } from 'utils/date'

import { fetchStudyTimeData } from 'services/DashboardService'
import {
  CloseIcon,
  IconButton,
  NavArrowLeftIcon,
  NavArrowRightIcon,
  BouncingLoader
} from 'examkrackers-components'
import { isNotNilOrEmpty } from 'utils/ramda'
import { useSelector } from 'react-redux'
import { getIsImpersonate } from 'modules/auth/ducks/selectors'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import eventEmitter from 'providers/eventEmitter'
import events from 'modules/learningTime/utils/events'

export const LearningTimeChart = ({ isOpened, handleIsClose, isFreeTrial }) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [labelsVisible, setLabelsVisible] = useState(false)
  const [dayLabelsVisible, setDayLabelsVisible] = useState(false)
  const [dayNumbers, setDayNumbers] = useState({
    1: [],
    2: [],
    3: []
  })
  const [currentPage, setCurrentPage] = useState(3)
  const [chartSerires, setChartSeries] = useState({
    1: [],
    2: [],
    3: []
  })
  const [ranges, setRanges] = useState({
    1: '',
    2: '',
    3: ''
  })
  const [chartConfig, setChartConfig] = useState({
    1: {},
    2: {},
    3: {}
  })
  const containerRef = useRef(null)
  const isImpersonate = useSelector(getIsImpersonate)
  const chartTitle = t('navigation.chart.chartTitle')

  const setLabelsForWeek = useMemo(
    () => (weekday, chart, chartTotalLeftOffset, topOffset) => {
      // Finds bar for specific week
      const weekdayBars = document.querySelector(
        `#learning-time-chart g[seriesName='${weekday}']`
      )

      // here for each bar a span is created with given week day and positioned absolutely to the chart container
      if (weekdayBars) {
        const barsList = Array.from(weekdayBars.children).filter((el, index) =>
          // @ts-ignore
          R.includes('apexcharts-bar-area', el.classList)
        )
        const firstLetterofWeekday = weekday.slice(0, 1)

        barsList.forEach((bar, index) => {
          const barLabel = document.createElement('div')
          const barLabelText = document.createTextNode(firstLetterofWeekday)
          const barTotalLeftOffset = bar.getBoundingClientRect().left
          const barLeftOffset = barTotalLeftOffset - chartTotalLeftOffset

          // @ts-ignore
          barLabel.style.cssText = `position: absolute; left: ${
            barLeftOffset - 4 + 'px'
          }; top: ${topOffset}px; width: ${'29px'}; font-size: ${'12px'};`
          barLabel.className = 'x-axis-dynamic-custom-labels'

          barLabel.appendChild(barLabelText)
          // @ts-ignore
          chart.appendChild(barLabel)
        })
      }
    },
    [chartConfig]
  )

  const setLabels = useMemo(
    () => () => {
      setLabelsVisible(true)
      const chart = document.getElementById('learning-time-chart')
      const chartXAxis = document.querySelector(
        '#learning-time-chart .apexcharts-xaxis'
      )
      // @ts-ignore
      const chartTotalLeftOffset = chart.getBoundingClientRect().left
      const topOffset =
        // @ts-ignore
        chartXAxis.getBoundingClientRect().top -
        chart.getBoundingClientRect().top

      setLabelsForWeek('Sun', chart, chartTotalLeftOffset, topOffset)
      setLabelsForWeek('Mon', chart, chartTotalLeftOffset, topOffset)
      setLabelsForWeek('Tue', chart, chartTotalLeftOffset, topOffset)
      setLabelsForWeek('Wed', chart, chartTotalLeftOffset, topOffset)
      setLabelsForWeek('Thu', chart, chartTotalLeftOffset, topOffset)
      setLabelsForWeek('Fri', chart, chartTotalLeftOffset, topOffset)
      setLabelsForWeek('Sat', chart, chartTotalLeftOffset, topOffset)
    },
    [setLabelsForWeek, labelsVisible]
  )

  const appendDayNumbers = useMemo(
    () => () => {
      const chart = document.getElementById('learning-time-chart')
      const weekdayLabels = document.querySelectorAll(
        '.x-axis-dynamic-custom-labels'
      )
      const labelsList = Array.from(weekdayLabels)

      const chartTotalLeftOffset = chart.getBoundingClientRect().left
      const chartTotalTopOffset = chart.getBoundingClientRect().top

      labelsList.forEach((label, index) => {
        const dayNumber = dayNumbers[currentPage][index] || ''

        const dayNumberElement = document.createElement('div')
        const dayNumberText = document.createTextNode(dayNumber)

        const labelLeftOffset = label.getBoundingClientRect().left
        const labelTopOffset = label.getBoundingClientRect().top
        const labelWidth = label.getBoundingClientRect().width
        const labelTotalLeftOffset = labelLeftOffset - chartTotalLeftOffset
        const labelTotalTopOffset = labelTopOffset - chartTotalTopOffset

        dayNumberElement.style.cssText = `position: absolute; left: ${
          labelTotalLeftOffset - 2
        }px; top: ${
          labelTotalTopOffset + 15
        }px; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; width: ${labelWidth}px;`
        dayNumberElement.className = 'x-axis-dynamic-custom-day-labels'

        dayNumberElement.appendChild(dayNumberText)
        chart.appendChild(dayNumberElement)
        setDayLabelsVisible(true)
      })
    },
    [dayNumbers, labelsVisible, dayLabelsVisible, isOpened, currentPage]
  )

  useOutsideClick(containerRef, handleIsClose)

  const handleChartDates = result => {
    const dates = result.data.dates.slice(-12)
    const formattedStartDates = dates.map(date => {
      const dateObj = noTimezoneDate(date)
      dateObj.setDate(dateObj.getDate() + 1)
      return format(dateObj, 'MMM d, yyyy')
    })
    const formattedEndDates = dates.map(date =>
      format(noTimezoneDate(date), 'MMM d, yyyy')
    )
    setRanges({
      1: `${formattedStartDates[0]}-${formattedEndDates[3]}`,
      2: `${formattedStartDates[3]}-${formattedEndDates[7]}`,
      3: `${formattedStartDates[7]}-${formattedEndDates[11]}`
    })
  }

  const getDaysBetweenDates = (
    startDate: string,
    endDate: string
  ): string[] => {
    const currentDate = noTimezoneDate(startDate)
    const lastDate = noTimezoneDate(endDate)

    // Calculate number of days between dates
    const daysDiff = Math.ceil(
      (lastDate.getTime() - currentDate.getTime()) / (1000 * 3600 * 24)
    )

    // If less than 28 days, adjust start date backwards
    if (daysDiff < 28) {
      const daysToAdd = 28 - daysDiff
      currentDate.setDate(currentDate.getDate() - daysToAdd)
    }

    // Add one day to start date
    currentDate.setDate(currentDate.getDate() + 1)

    const daysOfWeek: { [key: number]: number[] } = {
      0: [], // Sunday
      1: [], // Monday
      2: [], // Tuesday
      3: [], // Wednesday
      4: [], // Thursday
      5: [], // Friday
      6: [] // Saturday
    }

    let daysCount = 0
    while (daysCount < 28) {
      const dayOfWeek = currentDate.getDay()
      daysOfWeek[dayOfWeek].push(currentDate.getDate())
      currentDate.setDate(currentDate.getDate() + 1)
      daysCount++
    }

    // Combine all days in order from Sunday to Saturday
    return [
      ...daysOfWeek[0], // Sunday
      ...daysOfWeek[1], // Monday
      ...daysOfWeek[2], // Tuesday
      ...daysOfWeek[3], // Wednesday
      ...daysOfWeek[4], // Thursday
      ...daysOfWeek[5], // Friday
      ...daysOfWeek[6] // Saturday
    ].map(String) // Convert numbers to strings to fix type error
  }

  const handleDayNumbers = result => {
    const dates = result.data.dates.slice(-12)

    setDayNumbers({
      1: getDaysBetweenDates(dates[0], dates[3]),
      2: getDaysBetweenDates(dates[3], dates[7]),
      3: getDaysBetweenDates(dates[7], dates[11])
    })
  }

  const handleChartData = result => {
    const data = result.data.data.map(item => ({
      ...item,
      data: item.data.slice(-12)
    }))

    const organisedData = R.pipe(
      // @ts-ignore
      sortByWeekOrder,
      convertNullsToZero
      // @ts-ignore
    )(data)

    const dataByPage = {
      // @ts-ignore
      1: organisedData.map(item => ({
        ...item,
        data: item.data.slice(0, 4)
      })),
      // @ts-ignore
      2: organisedData.map(item => ({
        ...item,
        data: item.data.slice(3, 7)
      })),
      // @ts-ignore
      3: organisedData.map(item => ({
        ...item,
        data: item.data.slice(-4)
      }))
    }

    // @ts-ignore
    setChartSeries(dataByPage)
  }

  useEffect(() => {
    // @ts-ignore
    setChartConfig({
      1: TimeChart(chartSerires[1], chartTitle),
      2: TimeChart(chartSerires[2], chartTitle),
      3: TimeChart(chartSerires[3], chartTitle)
    })
  }, [chartSerires])

  const handleLoading = isLoading => {
    setIsLoading(isLoading)
    setTimeout(() => setIsLoading(false), 1000)
  }

  useEffect(() => {
    eventEmitter.on(events.learningTimeChartLoading, handleLoading)
    return () => {
      eventEmitter.off(events.learningTimeChartLoading, handleLoading)
    }
  }, [])

  useEffect(() => {
    if (isOpened && !labelsVisible) {
      fetchStudyTimeData()
        .then(result => {
          handleChartDates(result)
          handleChartData(result)
          handleDayNumbers(result)
          setTimeout(setLabels, 1000)
        })
        .catch(console.error)
    }
  }, [isOpened, labelsVisible])

  useEffect(() => {
    if (
      isOpened &&
      labelsVisible &&
      !dayLabelsVisible &&
      dayNumbers[currentPage].length > 0
    ) {
      appendDayNumbers()
    }
  }, [dayNumbers, labelsVisible, dayLabelsVisible, isOpened, currentPage])

  const handleChangePage = page => () => {
    handleLoading(true)
    setCurrentPage(page)
    setDayLabelsVisible(false)
    const elements = document.querySelectorAll(
      '.x-axis-dynamic-custom-day-labels'
    )
    elements.forEach(element => element.remove())
  }

  const GraphPlaceholder = isNotNilOrEmpty(chartSerires[currentPage]) && (
    <ChartContainer id='learning-time-chart' ref={containerRef}>
      <img
        className='salty-study-time'
        alt='image with salty looking into his watch'
        src='/assets/illustrations/SaltyStudyTime.svg'
      />
      <ApexChart
        id='learning-time-chart-svg'
        // @ts-ignore
        options={chartConfig[currentPage]}
        series={R.pathOr([], [currentPage, 'series'], chartConfig)}
        type='bar'
        width='719px'
        height='400px'
      />
      <ButtonsContainer>
        <IconButton
          icon={<NavArrowLeftIcon />}
          variant='filled'
          onClick={handleChangePage(currentPage - 1)}
          disabled={currentPage === 1}
        />

        <DateRange>{ranges[currentPage]}</DateRange>

        <IconButton
          type='button'
          icon={<NavArrowRightIcon />}
          variant='filled'
          onClick={handleChangePage(currentPage + 1)}
          disabled={currentPage === 3}
        />
      </ButtonsContainer>
      <div onClick={handleIsClose} className='learning-time-close'>
        <CloseIcon />
      </div>
      {isLoading && (
        <LoadingOverlay>
          <BouncingLoader />
        </LoadingOverlay>
      )}
    </ChartContainer>
  )

  return isOpened ? (
    <Container isFreeTrial={isFreeTrial} isImpersonate={isImpersonate}>
      <AlertWrapper>{GraphPlaceholder}</AlertWrapper>
    </Container>
  ) : null
}

export default LearningTimeChart

const Container = styled.div`
  position: fixed;
  top: ${({ isFreeTrial, isImpersonate }) =>
    isFreeTrial || isImpersonate ? '96px' : '64px'};
  left: -16px;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: ${({ theme }) => theme.zIndex.mainMenu + 10};

  .learning-time-close {
    position: absolute;
    right: 12.5px;
    top: 12.5px;
    cursor: pointer;
    font-size: 16px;
  }

  .salty-study-time {
    position: absolute;
    right: 90px;
    top: 43px;
  }
`

const AlertWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
`
const ChartContainer = styled.div`
  // this is needed for positioning labels that are append with JS code on mount
  position: relative !important;
  background: ${({ theme }) => theme.colors.backgrounds.main};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.chartShadow};
  overflow: hidden;
  height: 454px;
  width: 848px;
  border-radius: 5px;
  border: 2px solid ${({ theme }) => theme.colors.main.primary500};

  #learning-time-chart-svg > div {
    margin: 0 auto;
  }

  .apexcharts-datalabels {
    fill: ${({ theme }) => theme.colors.main.text}; !important;
  }

  .apexcharts-data-labels {
    rect {
      height: 35px !important;
      fill: ${({ theme }) => theme.colors.main.text}; !important;
      opacity: 0.95 !important;
    }

    text {
      transform: translateY(7px);
      fill: ${({ theme }) => theme.colors.main.white}; !important;
    }
  }

  // hides original X axis labels - but they are needed to keep the X axis area
  .xaxis-labels {
    opacity: 0;
  }
  .x-axis-dynamic-custom-labels {
    display: inline-block;
    color: ${({ theme }) => theme.colors.main.grey600};
    font-size: 12px;
    text-align: center;
  }
  .apexcharts-gridline {
    stroke: ${({ theme }) => theme.colors.main.grey300};
  }

  .apexcharts-bar-area {
    fill: ${({ theme }) => theme.colors.main.primary400};
  }

  .apexcharts-bar-area:hover {
    fill: ${({ theme }) => theme.colors.main.primary500};
    cursor: pointer;
  }

  #learning-time-chart-svg {
    g.apexcharts-datalabels {
      transform: translateX(40px);
    }
  }

  .apexcharts-data-labels {
    transition: all 100ms ${({ theme }) => theme.transitions.easing.easeInOut};
    opacity: 0;
  }
`
const ButtonsContainer = styled.div`
  position: absolute;
  top: 70px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
`

const DateRange = styled.div`
  font-weight: 700;
  font-size: 16px;
  margin: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.main.text};
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100% + 30px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.backgrounds.main};
  opacity: 0.7;
  z-index: 1000;
`
